import React, { useState, useRef, useCallback } from "react";
import { Cloud, Loader2 } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import api from "@/lib/api";
import WeatherStrip from "@/components/WeatherStrip";

/**
 * Hover/click button that lazily fetches and shows the 14-day weather forecast
 * for a given address. Uses Open-Meteo via /api/weather/forecast?address=...
 * The fetch is triggered on first hover and cached for the lifetime of the component.
 */
export default function LocationWeatherButton({ address, label = "Het weer op deze locatie", className = "" }) {
    const [days, setDays] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const fetchedRef = useRef(null); // stores the address that was last fetched

    const fetchOnce = useCallback(async () => {
        if (!address || fetchedRef.current === address) return;
        fetchedRef.current = address;
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get("/weather/forecast", { params: { address, days: 14 } });
            setDays(data?.days || []);
        } catch (e) {
            fetchedRef.current = null; // allow retry on next hover
            setError(e.response?.data?.detail || "Weer kon niet worden opgehaald");
        }
        setLoading(false);
    }, [address]);

    if (!address) return null;

    return (
        <HoverCard openDelay={120} closeDelay={80}>
            <HoverCardTrigger asChild>
                <button
                    type="button"
                    onMouseEnter={fetchOnce}
                    onFocus={fetchOnce}
                    onClick={fetchOnce}
                    className={`inline-flex items-center gap-2 h-8 px-3 rounded-sm text-xs font-medium border border-zinc-200 bg-[#FFFFFF] text-zinc-600 hover:text-zinc-950 hover:border-[#E5A93D]/40 hover:bg-[#E5A93D]/5 transition active:scale-[0.98] ${className}`}
                    data-testid="location-weather-btn"
                >
                    <Cloud className="h-3.5 w-3.5 text-[#E5A93D]" />
                    {label}
                </button>
            </HoverCardTrigger>
            <HoverCardContent
                align="start"
                sideOffset={8}
                className="w-[640px] max-w-[92vw] bg-[#FFFFFF] border border-zinc-200 text-zinc-950 p-4 shadow-2xl"
                data-testid="location-weather-popover"
            >
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Weersvoorspelling 14 dagen</div>
                        <div className="text-sm text-zinc-800 mt-0.5 truncate max-w-[420px]" title={address}>
                            {address}
                        </div>
                    </div>
                    <span className="text-[10px] text-zinc-500">bron Open-Meteo</span>
                </div>

                {loading && (
                    <div className="flex items-center gap-2 text-sm text-zinc-500 py-6 justify-center" data-testid="weather-loading">
                        <Loader2 className="h-4 w-4 animate-spin" /> Weer wordt opgehaald…
                    </div>
                )}
                {!loading && error && (
                    <div className="text-sm text-red-600 py-4 text-center" data-testid="weather-error">{error}</div>
                )}
                {!loading && !error && days && days.length === 0 && (
                    <div className="text-sm text-zinc-500 py-4 text-center">Geen voorspelling beschikbaar.</div>
                )}
                {!loading && !error && days && days.length > 0 && (
                    <>
                        <WeatherStrip days={days} />
                        <div className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
                            Tip: dagen met een lage regenkans en weinig wind zijn ideaal voor buitenshoots.
                        </div>
                    </>
                )}
            </HoverCardContent>
        </HoverCard>
    );
}
